import { useEffect, useState } from 'react';
import { HelpButton } from '../../components/HelpButton';
import { Link } from 'react-router';
import { myPartnersService, mediaUrl, STAFF_PAGE_SIZE } from '../../lib/api-service';
import type { PartnerDetail } from '../../lib/types';
import { ConfirmDialog, Pagination } from '../../components';
import { Loader2, Pencil, Check, X, Handshake, ShieldCheck, ShieldAlert, FileText, AlertTriangle } from 'lucide-react';

export default function MyPartners() {
  const [partners, setPartners] = useState<PartnerDetail[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<PartnerDetail>>({});
  const [saving, setSaving] = useState(false);
  const [legalFieldsUnlocked, setLegalFieldsUnlocked] = useState(false);
  const [showUnlockConfirm, setShowUnlockConfirm] = useState(false);

  useEffect(() => {
    setLoading(true);
    myPartnersService
      .list({ page })
      .then((data) => { setPartners(data.results); setTotalCount(data.count); })
      .catch(() => { setPartners([]); setTotalCount(0); })
      .finally(() => setLoading(false));
  }, [page]);

  function startEdit(partner: PartnerDetail) {
    setEditingId(partner.id);
    setLegalFieldsUnlocked(false);
    setEditForm({
      description: partner.description,
      contact_name: partner.contact_name,
      contact_phone: partner.contact_phone,
      contact_email: partner.contact_email,
      instagram: partner.instagram,
      legal_name: partner.legal_name,
      cif: partner.cif,
      legal_address: partner.legal_address,
      legal_representative_name: partner.legal_representative_name,
      legal_representative_role: partner.legal_representative_role,
    });
  }

  async function saveEdit() {
    if (!editingId) return;
    setSaving(true);
    try {
      const updated = await myPartnersService.update(editingId, editForm);
      setPartners((prev) => prev.map((p) => (p.id === editingId ? updated : p)));
      setEditingId(null);
    } catch {
      // silently fail
    } finally {
      setSaving(false);
    }
  }

  const inputClass =
    'w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20';

  return (
    <div>
      <div className="mb-6 flex items-center gap-2">
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
          Organizațiile mele
        </h1>
        <HelpButton to="/staff/documentatie/organizatiile-mele" />
      </div>

      {loading ? (
        <div className="flex min-h-[30vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[var(--vivid-purple)]" />
        </div>
      ) : partners.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
          Nu ai organizații asociate.
        </div>
      ) : (
        <div className="space-y-6">
          {partners.map((partner) => (
            <div key={partner.id} className="rounded-xl border bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center gap-4">
                {partner.logo ? (
                  <img src={mediaUrl(partner.logo)} alt="" className="h-16 w-16 rounded-xl border object-cover" />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-[var(--vivid-purple)]/10">
                    <Handshake className="h-8 w-8 text-[var(--vivid-purple)]" />
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
                      {partner.name}
                    </h2>
                    {partner.is_validated ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                        <ShieldCheck className="h-3 w-3" /> Validat
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                        <ShieldAlert className="h-3 w-3" /> Nevalidat
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                    {partner.legal_name || partner.status}
                  </p>
                </div>
                <div className="flex gap-2">
                  {editingId !== partner.id && (
                    <>
                      <Link
                        to={`/staff/dpa/${partner.id}`}
                        className="flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors hover:bg-gray-50"
                        style={{ fontFamily: 'var(--font-primary)' }}
                      >
                        <FileText className="h-4 w-4" />
                        DPA
                      </Link>
                      <button
                        onClick={() => startEdit(partner)}
                        className="flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors hover:bg-gray-50"
                        style={{ fontFamily: 'var(--font-primary)' }}
                      >
                        <Pencil className="h-4 w-4" />
                        Editează
                      </button>
                    </>
                  )}
                </div>
              </div>

              {editingId === partner.id ? (
                <div className="space-y-3">
                  <div>
                    <label className="mb-1 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                      Descriere
                    </label>
                    <textarea
                      value={editForm.description || ''}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      rows={3}
                      className={inputClass}
                      style={{ fontFamily: 'var(--font-secondary)' }}
                    />
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                      Date legale
                    </h3>
                    {partner.dpa_accepted_at && !legalFieldsUnlocked && (
                      <button
                        type="button"
                        onClick={() => setShowUnlockConfirm(true)}
                        className="flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-100"
                      >
                        <AlertTriangle className="h-3.5 w-3.5" />
                        Editează datele legale
                      </button>
                    )}
                  </div>
                  <p className="mb-3 text-xs text-muted-foreground">Necesare pentru semnarea DPA și accesul la datele participanților.</p>
                  {partner.dpa_accepted_at && !legalFieldsUnlocked ? (
                    <div className="grid grid-cols-1 gap-2 rounded-lg border bg-gray-50 p-4 sm:grid-cols-2">
                      <div>
                        <span className="text-xs text-muted-foreground">Denumire legală</span>
                        <p className="text-sm font-medium">{partner.legal_name || '—'}</p>
                      </div>
                      <div>
                        <span className="text-xs text-muted-foreground">CIF/CUI</span>
                        <p className="text-sm font-medium">{partner.cif || '—'}</p>
                      </div>
                      <div className="sm:col-span-2">
                        <span className="text-xs text-muted-foreground">Adresa sediului social</span>
                        <p className="text-sm font-medium">{partner.legal_address || '—'}</p>
                      </div>
                      <div>
                        <span className="text-xs text-muted-foreground">Reprezentant legal</span>
                        <p className="text-sm font-medium">{partner.legal_representative_name || '—'}</p>
                      </div>
                      <div>
                        <span className="text-xs text-muted-foreground">Funcția</span>
                        <p className="text-sm font-medium">{partner.legal_representative_role || '—'}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {legalFieldsUnlocked && (
                        <div className="sm:col-span-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                          Modificarea datelor legale va invalida DPA-ul. Va trebui să re-semnezi acordul.
                        </div>
                      )}
                      <div>
                        <label className="mb-1 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                          Denumire legală
                        </label>
                        <input
                          type="text"
                          value={editForm.legal_name || ''}
                          onChange={(e) => setEditForm({ ...editForm, legal_name: e.target.value })}
                          className={inputClass}
                          style={{ fontFamily: 'var(--font-secondary)' }}
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                          CIF/CUI
                        </label>
                        <input
                          type="text"
                          value={editForm.cif || ''}
                          onChange={(e) => setEditForm({ ...editForm, cif: e.target.value })}
                          className={inputClass}
                          style={{ fontFamily: 'var(--font-secondary)' }}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="mb-1 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                          Adresa sediului social
                        </label>
                        <input
                          type="text"
                          value={editForm.legal_address || ''}
                          onChange={(e) => setEditForm({ ...editForm, legal_address: e.target.value })}
                          className={inputClass}
                          style={{ fontFamily: 'var(--font-secondary)' }}
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                          Reprezentant legal (nume)
                        </label>
                        <input
                          type="text"
                          value={editForm.legal_representative_name || ''}
                          onChange={(e) => setEditForm({ ...editForm, legal_representative_name: e.target.value })}
                          className={inputClass}
                          style={{ fontFamily: 'var(--font-secondary)' }}
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                          Funcția reprezentantului
                        </label>
                        <input
                          type="text"
                          value={editForm.legal_representative_role || ''}
                          onChange={(e) => setEditForm({ ...editForm, legal_representative_role: e.target.value })}
                          className={inputClass}
                          style={{ fontFamily: 'var(--font-secondary)' }}
                          placeholder="ex: Președinte, Administrator"
                        />
                      </div>
                    </div>
                  )}

                  <h3 className="mt-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                    Contact
                  </h3>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div>
                      <label className="mb-1 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                        Persoană de contact
                      </label>
                      <input
                        type="text"
                        value={editForm.contact_name || ''}
                        onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })}
                        className={inputClass}
                        style={{ fontFamily: 'var(--font-secondary)' }}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                        Telefon
                      </label>
                      <input
                        type="tel"
                        value={editForm.contact_phone || ''}
                        onChange={(e) => setEditForm({ ...editForm, contact_phone: e.target.value })}
                        className={inputClass}
                        style={{ fontFamily: 'var(--font-secondary)' }}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                        Email
                      </label>
                      <input
                        type="email"
                        value={editForm.contact_email || ''}
                        onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })}
                        className={inputClass}
                        style={{ fontFamily: 'var(--font-secondary)' }}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                        Instagram
                      </label>
                      <input
                        type="text"
                        value={editForm.instagram || ''}
                        onChange={(e) => setEditForm({ ...editForm, instagram: e.target.value })}
                        placeholder="@nume_cont sau link complet"
                        className={inputClass}
                        style={{ fontFamily: 'var(--font-secondary)' }}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={saveEdit}
                      disabled={saving}
                      className="flex items-center gap-1.5 rounded-lg bg-[var(--vivid-purple)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--vivid-purple)]/90 disabled:opacity-60"
                      style={{ fontFamily: 'var(--font-primary)' }}
                    >
                      <Check className="h-4 w-4" />
                      {saving ? 'Se salvează...' : 'Salvează'}
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="flex items-center gap-1.5 rounded-lg border px-4 py-2 text-sm font-medium transition-colors hover:bg-gray-50"
                      style={{ fontFamily: 'var(--font-primary)' }}
                    >
                      <X className="h-4 w-4" />
                      Anulează
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2 text-sm" style={{ fontFamily: 'var(--font-secondary)' }}>
                  {partner.description && <p>{partner.description}</p>}
                  <div className="flex flex-wrap gap-4 text-muted-foreground">
                    {partner.contact_name && <span>Contact: {partner.contact_name}</span>}
                    {partner.contact_phone && <span>Tel: {partner.contact_phone}</span>}
                    {partner.contact_email && <span>Email: {partner.contact_email}</span>}
                    {partner.instagram && (
                      <a
                        href={`https://instagram.com/${partner.instagram}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-foreground"
                      >
                        Instagram: @{partner.instagram}
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Pagination page={page} totalCount={totalCount} pageSize={STAFF_PAGE_SIZE} onPageChange={setPage} />

      <ConfirmDialog
        open={showUnlockConfirm}
        onOpenChange={(open) => !open && setShowUnlockConfirm(false)}
        onConfirm={() => {
          setLegalFieldsUnlocked(true);
          setShowUnlockConfirm(false);
        }}
        title="Editează datele legale"
        description="Modificarea datelor legale va invalida Acordul de Prelucrare a Datelor (DPA). După salvare, va trebui să re-semnezi DPA-ul pentru a avea din nou acces la datele participanților."
      />
    </div>
  );
}
